import React, { Fragment } from 'react';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { uniqWith } from 'lodash';
import I18n from 'i18n-js';

import Breadcrumbs from '@reverbdotcom/commons/src/components/breadcrumbs';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { findProductFamilySpec } from '@reverbdotcom/commons/src/product_family_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import {
  Item2FallbackBreadcrumbsDataFragment,
  Category,
  Core_Listing_LayoutQuery,
} from '@reverbdotcom/commons/src/gql/graphql';

import URLHelpers, { withWebHostAndRegion } from './shared/url_helpers';

type Listing = Core_Listing_LayoutQuery['listing'];

interface IProps {
  listing: Listing;
}

export const Item2FallbackBreadcrumbsFragment = gql`
  fragment Item2FallbackBreadcrumbsData on Listing {
    _id
    id
    make
    brandSlug
    categories {
      _id
      id
      slug
      name
      rootSlug
      webLink {
        href
      }
      cmsLink {
        href
      }
    }
    csp {
      _id
      id
      specs {
        productSpecs {
          _id
          type
          name
          value
          path
        }
      }
    }
  }
`;

export const Item2BreadcrumbsFragment = gql`
  fragment Item2BreadcrumbsData on Listing {
    _id
    breadcrumbs {
      title
      url
    }
    csp {
      _id
      slug
      title
    }
  }
`;

export function componentClickEvent(displayStyle: string, path:string) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: 'Item2Breadcrumbs',
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

export function CategoryBreadcrumb(props: { cat: Item2FallbackBreadcrumbsDataFragment['categories'][0], idx: number }) {
  let { href } = props.cat.webLink;
  if (props.cat.cmsLink?.href) {
    href = props.cat.cmsLink.href;
  }

  return (
    <>
      <CoreLink
        to={href}
        clickEvent={componentClickEvent('categoryBreadcrumb', href)}
        itemProp="item"
      >
        <span itemProp="name">
          {props.cat.name}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${props.idx + 1}`} />
    </>
  );
}

export function getCategories(listing) {
  return uniqWith(
    listing.categories,
    (a: Category, b: Category) => (a.rootSlug === b.rootSlug),
  );
}

export function getBrandBreadcrumbParams(listing) {
  const categories = getCategories(listing);
  const childCat = categories.find((c) => !!c.rootSlug.length);

  if (!childCat) {
    return { product_type: categories[0]?.slug };
  }
  return {
    product_type: childCat.rootSlug,
    category: childCat.slug,
  };
}

export function BrandBreadcrumb({ listing }: IProps) {
  const params = { ...getBrandBreadcrumbParams(listing) };

  return (
    <>
      <CoreLink
        to={URLHelpers.brandPath(listing.brandSlug, params)}
        clickEvent={componentClickEvent('brandBreadcrumb', URLHelpers.brandPath(listing.brandSlug, params))}
        itemProp="item"
      >
        <span itemProp="name">
          {listing.make}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${getCategories(listing).length + 1}`} />
    </>
  );
}

export function ProductFamilyBreadcrumb({ listing }: IProps) {
  const productFamily = findProductFamilySpec(listing.csp?.specs?.productSpecs);
  const mparticleEvent: MParticleEvent = {
    componentName: 'ProductFamilyCTA',
    eventName: MParticleEventName.ComponentView,
  };

  useViewTracking(mparticleEvent, !!productFamily);

  return (
    <>
      <CoreLink
        to={productFamily.path}
        data-product-family-link
        clickEvent={componentClickEvent('productFamilyBreadcrumb', productFamily.path)}
        target="_top"
        itemProp="item"
      >
        <span itemProp="name">
          {productFamily.value}
        </span>
      </CoreLink>
      <meta itemProp="position" content={`${getCategories(listing).length + 2}`} />
    </>
  );
}

function makeBreadcrumbClickEvent(listingId: string, breadcrumbUrl: string) {
  return {
    eventName: MParticleEventName.ClickedBreadcrumb,
    componentName: 'Item2Breadcrumbs',
    entityType: 'url',
    entityId: breadcrumbUrl,
    listingId,
  };
}

export function Item2Breadcrumbs({ listing }: IProps) {
  const user = useUser();

  if (!listing) return null;

  const { id, breadcrumbs } = listing;

  if (breadcrumbs) {
    return (
      <div className="item2-breadcrumbs">
        <Breadcrumbs shouldScroll>
          <>
            <CoreLink
              to={{ pathname: '/' }}
            >
              <span itemProp="name">
                {I18n.t('commons.home')}
              </span>
            </CoreLink>
            <meta itemProp="position" content="1" />
            <meta itemProp="item" content={withWebHostAndRegion('/', user.urlRegion)} />
          </>
          {breadcrumbs.map((breadcrumb, index) => (
            <Fragment key={breadcrumb.url}>
              <CoreLink
                to={breadcrumb.url}
                target="_top"
                clickEvent={makeBreadcrumbClickEvent(id, breadcrumb.url)}
              >
                <span itemProp="name">
                  {breadcrumb.title}
                </span>
              </CoreLink>
              <meta itemProp="position" content={`${(index + 2)}`} />
              <meta itemProp="item" content={withWebHostAndRegion(breadcrumb.url, user.urlRegion)} />
            </Fragment>
          ))}
          {listing.csp && (
            <>
              <CoreLink
                to={URLHelpers.cspPath(listing.csp.slug)}
              >
                <span itemProp="name">
                  {listing.csp.title}
                </span>
              </CoreLink>
              <meta itemProp="position" content={`${(breadcrumbs.length + 2)}`} />
              <meta
                itemProp="item"
                content={withWebHostAndRegion(URLHelpers.cspPath(listing.csp.slug), user.urlRegion)}
              />
            </>
          )}
        </Breadcrumbs>
      </div>
    );
  }
  const renderBrandBreadcrumb = listing?.brandSlug;
  const productFamily = findProductFamilySpec(listing?.csp?.specs?.productSpecs);
  const renderProductFamilyBreadcrumb = !!productFamily;

  // if breadcrumbs is not present on the listing, render the fallback breadcrumbs
  return (
    <div className="item2-breadcrumbs">
      <Breadcrumbs shouldScroll>
        {getCategories(listing).map((cat, idx) => (
          <CategoryBreadcrumb cat={cat} key={cat.id} idx={idx} />
        ))}
        {renderBrandBreadcrumb && <BrandBreadcrumb listing={ listing } />}
        {renderProductFamilyBreadcrumb && <ProductFamilyBreadcrumb listing={listing} />}
      </Breadcrumbs>
    </div>
  );
}
